// Number picker: browse a random selection of free 12-digit numbers by tier, pick one and buy it with Kal coins.
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { formatICN } from '../lib/kaltiq.js';

const fmt = n => Number(n).toLocaleString();
const TIER_LABELS = {general: 'General', vanity: 'Vanity', premium: 'Premium'};

function newRequestId() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() :
    Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export default class AccNumberView extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      tier: 'general',
      offers: [],
      loading: true,
      wallet: null,
      selected: null,
      claiming: false,
      error: null,
      done: null
    };
    // One id per intent: if the request is retried (network glitch) the server will not charge twice.
    this.requestId = null;

    this.loadOffers = this.loadOffers.bind(this);
    this.confirm = this.confirm.bind(this);
    this.skip = this.skip.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    this.loadWallet(true);
    this.loadOffers('general');
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  async loadWallet(retryIfEmpty) {
    try {
      const wallet = await this.props.kaltiq.wallet();
      if (!this._mounted) return;
      this.setState({wallet});
      // Right after signup the free coins are credited a moment later; look once more.
      if (retryIfEmpty && wallet.balance === 0 && !wallet.icn) {
        setTimeout(() => this._mounted && this.loadWallet(false), 1500);
      }
    } catch (err) {
      if (this._mounted) this.setState({error: err.message});
    }
  }

  async loadOffers(tier) {
    this.setState({tier, loading: true, selected: null, error: null});
    try {
      const offers = await this.props.kaltiq.offers(tier, 8);
      if (this._mounted && this.state.tier === tier) {
        this.setState({offers, loading: false});
      }
    } catch (err) {
      if (this._mounted) this.setState({loading: false, offers: [], error: err.message});
    }
  }

  select(offer) {
    this.requestId = newRequestId();
    this.setState({selected: offer, error: null});
  }

  async confirm() {
    const offer = this.state.selected;
    if (!offer || this.state.claiming) return;
    this.setState({claiming: true, error: null});
    try {
      const res = await this.props.kaltiq.claim(offer.number, this.requestId);
      if (this._mounted) {
        this.setState({claiming: false, done: res, selected: null, wallet: Object.assign({}, this.state.wallet, {balance: res.balance, icn: res})});
      }
    } catch (err) {
      if (!this._mounted) return;
      if (err.status === 409) {
        // Someone was faster: refresh the selection.
        this.setState({claiming: false});
        this.loadOffers(this.state.tier);
        this.setState({error: err.message + '. Here are some other numbers.'});
      } else {
        this.setState({claiming: false, error: err.status === 402 ? 'Not enough Kal coins for this number.' : err.message});
      }
    }
  }

  skip() {
    // Remember the choice for this account so we don't ask again at every login.
    const w = this.state.wallet;
    if (w && w.user) {
      try { localStorage.setItem('kaltiq-skip-number-' + w.user, '1'); } catch (_) { /* ignore */ }
    }
    this.props.onNavigate('contacts');
  }

  render() {
    const {tier, offers, loading, wallet, selected, claiming, error, done} = this.state;
    const balance = wallet ? wallet.balance : null;
    const hasNumber = wallet && wallet.icn;

    if (done) {
      return (
        <div className="scrollable-panel kq-panel">
          <div className="panel-form-column kq-success">
            <i className="material-icons kq-success-icon">check_circle</i>
            <div className="kq-muted">
              <FormattedMessage id="kq_number_is_yours" defaultMessage="Your number is" description="KalTiQ: success caption" />
            </div>
            <tt className="kq-number kq-number-big">{formatICN(done.number)}</tt>
            <div className="kq-muted">
              {done.charged ?
                <FormattedMessage id="kq_spent" defaultMessage="{price} Kal coins spent. Balance: {balance}"
                  description="KalTiQ: success detail" values={{price: fmt(done.price), balance: fmt(done.balance)}} /> :
                <FormattedMessage id="kq_balance_now" defaultMessage="Balance: {balance} Kal coins"
                  description="KalTiQ: success detail" values={{balance: fmt(done.balance)}} />}
            </div>
            <div className="dialog-buttons kq-actions">
              <button type="button" className="primary" onClick={() => this.props.onNavigate('wallet')}>
                <FormattedMessage id="kq_done" defaultMessage="Done" description="KalTiQ: button" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="scrollable-panel kq-panel">
        <div className="panel-form-column">
          <div className="kq-muted">
            <FormattedMessage id="kq_pick_intro"
              defaultMessage="Pick a 12-digit number. Anyone on KalTiQ can call you on it."
              description="KalTiQ: number picker intro" />
          </div>
          <div className="kq-balance-line">
            <FormattedMessage id="kq_your_balance" defaultMessage="Your balance:" description="KalTiQ: label" />{' '}
            <b>{balance === null ? '…' : fmt(balance)}</b>{' '}
            <FormattedMessage id="kq_coins" defaultMessage="coins" description="KalTiQ: unit" />
          </div>
          {hasNumber ?
            <div className="kq-muted small">
              <FormattedMessage id="kq_current_number" defaultMessage="Current number: {number}. Choosing a new one releases it."
                description="KalTiQ: warning when changing number" values={{number: formatICN(wallet.icn.number)}} />
            </div> : null}
        </div>

        <div className="kq-tabs" role="tablist">
          {['general', 'vanity', 'premium'].map(t => (
            <button key={t} type="button" role="tab" aria-selected={tier === t}
              className={'kq-tab' + (tier === t ? ' active' : '')} onClick={() => this.loadOffers(t)}>
              {TIER_LABELS[t]}
            </button>))}
        </div>

        <div className="panel-form-column">
          {error ? <div className="kq-error" role="alert">{error}</div> : null}
          {loading ? <div className="kq-muted"><FormattedMessage id="loading_note" defaultMessage="Loading..." description="Message shown when component is loading" /></div> : null}
          {!loading && offers.length == 0 && !error ?
            <div className="kq-muted">
              <FormattedMessage id="kq_no_offers" defaultMessage="No numbers are available in this category right now."
                description="KalTiQ: empty offer list" />
            </div> : null}
          <ul className="kq-offers">
            {offers.map(o => {
              const affordable = balance === null || balance >= o.price;
              return (
                <li key={o.number}>
                  <button type="button" className={'kq-offer' + (selected && selected.number === o.number ? ' selected' : '') + (affordable ? '' : ' unaffordable')}
                    onClick={() => this.select(o)} aria-pressed={!!selected && selected.number === o.number}>
                    <tt className="kq-number">{o.display || formatICN(o.number)}</tt>
                    <span className="kq-price">{fmt(o.price)} <small>coins</small></span>
                  </button>
                </li>);
            })}
          </ul>
          <a href="#" className="flat-button" onClick={e => {e.preventDefault(); this.loadOffers(tier);}}>
            <i className="material-icons">shuffle</i>&nbsp;
            <FormattedMessage id="kq_shuffle" defaultMessage="Show other numbers" description="KalTiQ: reload offers" />
          </a>
        </div>

        {selected ?
          <div className="panel-form-column kq-confirm">
            <div>
              <FormattedMessage id="kq_confirm_q" defaultMessage="Get {number} for {price} coins?"
                description="KalTiQ: confirmation question" values={{number: <tt className="kq-number">{formatICN(selected.number)}</tt>, price: <b>{fmt(selected.price)}</b>}} />
            </div>
            {balance !== null ?
              <div className="kq-muted small">
                {balance >= selected.price ?
                  <FormattedMessage id="kq_balance_after" defaultMessage="Balance after: {balance}" description="KalTiQ: info" values={{balance: fmt(balance - selected.price)}} /> :
                  <FormattedMessage id="kq_short_by" defaultMessage="You need {missing} more coins." description="KalTiQ: info" values={{missing: fmt(selected.price - balance)}} />}
              </div> : null}
            <div className="dialog-buttons">
              <button type="button" className="secondary" onClick={() => this.setState({selected: null})}>
                <FormattedMessage id="button_cancel" defaultMessage="Cancel" description="Button [Cancel]" />
              </button>
              <button type="button" className="primary" disabled={claiming || (balance !== null && balance < selected.price)} onClick={this.confirm}>
                {claiming ?
                  <FormattedMessage id="kq_working" defaultMessage="Working…" description="KalTiQ: button while busy" /> :
                  <FormattedMessage id="kq_get_number" defaultMessage="Get this number" description="KalTiQ: button" />}
              </button>
            </div>
          </div> : null}

        {!hasNumber ?
          <React.Fragment>
            <div className="hr" />
            <div className="panel-form-column">
              <a href="#" className="flat-button" onClick={e => {e.preventDefault(); this.skip();}}>
                <FormattedMessage id="kq_skip" defaultMessage="Skip for now" description="KalTiQ: skip number selection" />
              </a>
            </div>
          </React.Fragment> : null}
      </div>
    );
  }
};
